import { Component, computed, inject, signal } from '@angular/core';
import { Route } from '../../utils/route';
import { StatisticalCardComponent } from '../../components/card/card.component';
import { DictionaryService } from '../../services/dictionary.service';
import { AuthenticationService } from '../../services/authentication.service';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../services/config.service';
import { TabsComponent } from '../../components/tabs/tabs.component';
import { provideIcons } from '@ng-icons/core';
import { SelectComponent } from '../../components/select/select.component';
import { ButtonDirective } from '../../components/button/button.directive';
import {
  iconoirOpenInWindow,
  iconoirPhone,
  iconoirDatabaseScript,
  iconoirUser,
  iconoirGroup,
  iconoirHandbag,
  iconoirHealthShield,
  iconoirRefresh,
} from '@ng-icons/iconoir';
import { SalesComponent } from './sales/sales.component';
import { ProductsComponent } from './products/products.component';
import { SupervisorsComponent } from './supervisors/supervisors.component';
import { getReportsForRole } from '../../utils/reports';
import { ReportsService } from '../../services/reports.service';
import { VisitsService } from '../../services/visits.service';
import {
  AccordionContent,
  VisitsStatistics,
} from '../../interfaces/apis/visits-statistics-dashboard';
import { finalize } from 'rxjs';
import {
  DASHBOARD_REPORT_ENDPOINTS,
  DashboardReportKey,
  VISITS_BREAKDOWN_CONFIG,
} from './dashboard.constants';
import { FilterOption } from '../../components/table/table.interface';

@Component({
  selector: 'app-dashboard',
  imports: [
    StatisticalCardComponent,
    TabsComponent,
    SelectComponent,
    ButtonDirective,
    SalesComponent,
    ProductsComponent,
    SupervisorsComponent,
  ],
  providers: [
    provideIcons({
      iconoirOpenInWindow,
      iconoirPhone,
      iconoirDatabaseScript,
      iconoirUser,
      iconoirGroup,
      iconoirHandbag,
      iconoirHealthShield,
      iconoirRefresh,
    }),
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent extends Route {
  protected dictionary = inject(DictionaryService);
  protected authService = inject(AuthenticationService);
  protected http = inject(HttpClient);
  protected configService = inject(ConfigService);
  protected reportsService = inject(ReportsService);
  protected visitsService = inject(VisitsService);

  // Visits statistics state
  private visitsStats = signal<VisitsStatistics | null>(null);
  isLoadingVisits = signal(true);
  hasVisitsError = signal(false);

  // Computed values from statistics
  totalVisits = computed(() => this.visitsStats()?.totalVisits ?? 0);
  totalCalls = computed(() => this.visitsStats()?.totalCalls ?? 0);
  avgCallDuration = computed(
    () => this.visitsStats()?.avgCallDuration ?? '00:00'
  );

  // Accordion content for visits breakdown
  visitsDetails = computed<AccordionContent | undefined>(() => {
    const stats = this.visitsStats();
    if (!stats) return undefined;

    return {
      sub: `${stats.totalCalls} ${this.dictionary.get('Calls')}`,
      items: VISITS_BREAKDOWN_CONFIG.map(({ type, icon, title }) => ({
        icon,
        title: this.dictionary.get(title),
        description: `${stats.visitsBreakdown[type]} ${this.dictionary.get(
          'Visits'
        )}`,
      })),
    };
  });

  private reportsKeys: DashboardReportKey[] = [];
  reports: string[] = [];

  selectedReport: DashboardReportKey | null = null;

  isDownloadingReport = false;

  async ngOnInit(): Promise<void> {
    const policy = this.authService.currentUserPolicy;

    const companyId = policy?.companyId;

    const role = policy?.role;

    // Get report keys and translate them
    this.reportsKeys = getReportsForRole(role, companyId);
    this.reports = this.reportsKeys.map((key) => this.dictionary.get(key));

    // Fetch visits statistics
    this.loadVisitsStatistics();
  }

  /**
   * Loads visits statistics from the API
   */
  private loadVisitsStatistics(): void {
    const fromDate = this.configService.from();
    const toDate = this.configService.to();

    if (!fromDate || !toDate) {
      this.isLoadingVisits.set(false);
      return;
    }

    this.isLoadingVisits.set(true);
    this.visitsService
      .getVisitsStatistics(fromDate, toDate)
      .pipe(finalize(() => this.isLoadingVisits.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data?.statistics) {
            this.visitsStats.set(response.data.statistics);
            this.hasVisitsError.set(false);
          } else {
            this.hasVisitsError.set(true);
          }
        },
        error: () => {
          this.hasVisitsError.set(true);
        },
      });
  }

  /**
   * Handler for selected report from template select
   * Maps translated value back to original key
   */
  setReportType(selected: FilterOption): void {
    const translatedValue = selected?.value;
    const index = this.reports.map((option) => option).indexOf(translatedValue);
    this.selectedReport = index !== -1 ? this.reportsKeys[index] : null;
  }

  onExportClick(): void {
    if (!this.selectedReport) {
      return;
    }

    const fromDate = this.configService.from();
    const toDate = this.configService.to();

    if (!fromDate || !toDate) {
      return;
    }

    const reportPath = DASHBOARD_REPORT_ENDPOINTS[this.selectedReport];

    if (!reportPath) {
      return;
    }

    const reportObservable = this.reportsService.getDashboardReport(
      reportPath,
      fromDate,
      toDate
    );

    this.isDownloadingReport = true;
    reportObservable.subscribe({
      next: (response) => {
        this.isDownloadingReport = false;
        if (response.success && response.data?.reportURL) {
          this.reportsService.downloadReport(response.data.reportURL);
        }
      },
      error: () => {
        this.isDownloadingReport = false;
      },
    });
  }

  /**
   * Retry loading visits statistics
   */
  retryLoadVisits(): void {
    this.hasVisitsError.set(false);
    this.loadVisitsStatistics();
  }
}
